import { useEffect, useMemo, useState } from "react";
import { StatusBadge } from "@leylion/design-system";
import type { OpenOrderCard } from "../api/cashier-types";

type Language = "ar" | "en";

type Props = {
  language: Language;
  order: OpenOrderCard;
  active: boolean;
  formatMoney: (minor: number, currency?: string) => string;
  onSelect: () => void;
  onPrintPreBill: () => void;
  printingPreBill?: boolean;
};

function normalized(value: string | null | undefined): string | null {
  const trimmed = value?.trim();
  return trimmed ? trimmed : null;
}

function statusTone(status: OpenOrderCard["status"]): "neutral" | "info" | "success" | "warning" {
  if (status === "Ready") return "success";
  if (status === "Held") return "warning";
  if (status === "SentToKitchen") return "info";
  return "neutral";
}

function formatPreparationDuration(totalSeconds: number): string {
  const safeSeconds = Math.max(0, Math.floor(totalSeconds));
  const hours = Math.floor(safeSeconds / 3600);
  const minutes = Math.floor((safeSeconds % 3600) / 60);
  const seconds = safeSeconds % 60;
  const minutePart = String(minutes).padStart(2, "0");
  const secondPart = String(seconds).padStart(2, "0");
  return hours > 0
    ? `${String(hours).padStart(2, "0")}:${minutePart}:${secondPart}`
    : `${minutePart}:${secondPart}`;
}

const displayOrderNumber = (value: string) =>
  /^\d+$/.test(value) ? `#${value}` : value;

export function CashierOpenOrderCard({
  language,
  order,
  active,
  formatMoney,
  onSelect,
  onPrintPreBill,
  printingPreBill = false
}: Props) {
  const ar = language === "ar";
  const localizedTableName = normalized(
    ar
      ? order.tableNameAr ?? order.tableNameEn
      : order.tableNameEn ?? order.tableNameAr
  );
  const tableNumber = normalized(order.tableCode) ?? localizedTableName;
  const customerName = normalized(order.contactName) ?? normalized(order.customerName);
  const customerPhone = normalized(order.contactPhone) ?? normalized(order.customerPhone);
  const vehicleNumber = normalized(order.vehicleNumber);
  const isDineIn = order.serviceType === "DineIn";
  const identityMissing = isDineIn
    ? !tableNumber || !order.guestCount
    : order.serviceType === "Delivery"
      ? !customerName
      : false;

  const [clockNow, setClockNow] = useState(() => Date.now());
  const orderIsTerminal = order.status === "Completed"
    || order.status === "Cancelled"
    || order.status === "Refunded";
  const timerIsRunning = Boolean(
    !orderIsTerminal && order.preparationStartedAt && !order.preparationCompletedAt
  );
  useEffect(() => {
    if (!timerIsRunning) return undefined;
    setClockNow(Date.now());
    const interval = window.setInterval(() => setClockNow(Date.now()), 1000);
    return () => window.clearInterval(interval);
  }, [timerIsRunning, order.preparationStartedAt]);

  const preparationSeconds = useMemo(() => {
    if (order.preparationDurationSeconds !== null) {
      return Math.max(0, order.preparationDurationSeconds);
    }
    if (orderIsTerminal || !order.preparationStartedAt) return null;
    const startedAt = new Date(order.preparationStartedAt).getTime();
    const completedAt = order.preparationCompletedAt
      ? new Date(order.preparationCompletedAt).getTime()
      : clockNow;
    if (!Number.isFinite(startedAt) || !Number.isFinite(completedAt)) return null;
    return Math.max(0, Math.floor((completedAt - startedAt) / 1000));
  }, [
    clockNow,
    order.preparationCompletedAt,
    order.preparationDurationSeconds,
    order.preparationStartedAt,
    orderIsTerminal
  ]);
  const preparationIsCritical = preparationSeconds !== null
    && preparationSeconds > order.preparationCriticalAfterSeconds;

  const serviceLabels: Record<OpenOrderCard["serviceType"], { ar: string; en: string }> = {
    WalkIn: { ar: "استلام", en: "Pickup" },
    DineIn: { ar: "داخلي", en: "Dine-in" },
    Takeaway: { ar: "سفري", en: "Takeaway" },
    Delivery: { ar: "توصيل", en: "Delivery" }
  };
  const statusLabels: Record<OpenOrderCard["status"], { ar: string; en: string }> = {
    Draft: { ar: "مسودة", en: "Draft" },
    Held: { ar: "معلّق", en: "Held" },
    SentToKitchen: { ar: "أُرسل للتحضير", en: "Sent for preparation" },
    Ready: { ar: "جاهز", en: "Ready" },
    Completed: { ar: "مكتمل", en: "Completed" },
    Cancelled: { ar: "ملغي", en: "Cancelled" },
    Refunded: { ar: "مسترجع", en: "Refunded" }
  };
  const serviceLabel = serviceLabels[order.serviceType][ar ? "ar" : "en"];
  const statusLabel = statusLabels[order.status][ar ? "ar" : "en"];
  const preparationLabel = order.status === "Ready"
    ? ar ? "التحضير · جاهز" : "Preparation · Ready"
    : order.status === "SentToKitchen"
      ? ar ? "التحضير · جارٍ" : "Preparation · Preparing"
      : ar ? "التحضير · لم يُرسل" : "Preparation · Not sent";
  const previewLines = order.lines.slice(0, 2);
  const remainingItems = Math.max(0, order.itemCount - previewLines.length);

  const accessibleIdentity = isDineIn
    ? tableNumber ?? (ar ? "طاولة غير محددة" : "Table not assigned")
    : customerName ?? (ar ? "عميل غير محدد" : "Customer not identified");
  const scheduledFulfillmentLabel = order.scheduledFulfillmentAt
    ? new Date(order.scheduledFulfillmentAt).toLocaleString(ar ? "ar-AE" : "en-AE")
    : null;

  return (
    <article
      className={`cashier-order-card is-${order.serviceType.toLowerCase()}${
        active ? " is-active" : ""
      }${identityMissing ? " is-missing-identity" : ""}${order.isPreOrder ? " is-preorder" : ""}`}
      data-service-type={order.serviceType}
    >
      <button
        type="button"
        className="cashier-order-card__select"
        data-testid={`restore-order-${order.orderId}`}
        aria-label={`${order.isPreOrder ? `${ar ? "طلب مسبق" : "Pre-order"} ${order.bookingNumber ?? ""}. ` : ""}${serviceLabel}: ${accessibleIdentity}. ${displayOrderNumber(order.orderNumber)}. ${formatMoney(order.totalMinor, order.currency)}`}
        onClick={onSelect}
      >
      <div className="cashier-order-card__topline">
        <span className="cashier-order-card__service">{serviceLabel}</span>
        <span className="cashier-order-card__topline-actions">
          {preparationSeconds !== null ? (
            <span
              className={`cashier-order-card__timer${
                preparationIsCritical ? " is-critical" : ""
              }${timerIsRunning ? " is-running" : " is-stopped"}`}
              data-testid={`open-order-card-preparation-timer-${order.orderId}`}
              title={ar ? "وقت تحضير الطلب" : "Order preparation time"}
              aria-label={`${ar ? "وقت التحضير" : "Preparation time"}: ${formatPreparationDuration(preparationSeconds)}`}
            >
              <span aria-hidden="true">⏱</span>
              <strong dir="ltr">{formatPreparationDuration(preparationSeconds)}</strong>
            </span>
          ) : null}
          <StatusBadge tone={statusTone(order.status)}>{statusLabel}</StatusBadge>
        </span>
      </div>
      <small className="cashier-order-card__preparation" data-testid={`preparation-status-${order.orderId}`}>
        {preparationLabel}
      </small>

      {order.isPreOrder ? (
        <div
          className="cashier-order-card__preorder"
          data-testid={`open-order-card-preorder-${order.orderId}`}
        >
          <span>
            <small>{ar ? "طلب مسبق" : "Pre-order"}</small>
            <strong>{order.bookingNumber ?? displayOrderNumber(order.orderNumber)}</strong>
          </span>
          <span>
            <small>{ar ? "موعد الاستلام" : "Fulfillment"}</small>
            <strong>{scheduledFulfillmentLabel ?? "—"}</strong>
          </span>
        </div>
      ) : null}

      {isDineIn ? (
        <section className="cashier-order-card__identity cashier-order-card__identity--table">
          <span className="cashier-order-card__eyebrow">
            {ar ? "رقم الطاولة" : "Table number"}
          </span>
          <strong
            className="cashier-order-card__primary cashier-order-card__primary--table"
            data-testid={`open-order-card-table-number-${order.orderId}`}
          >
            {tableNumber ?? (ar ? "غير محددة" : "Not assigned")}
          </strong>
          <div className="cashier-order-card__identity-meta">
            {localizedTableName && localizedTableName !== tableNumber ? (
              <span>{localizedTableName}</span>
            ) : null}
            <span>
              {ar ? "الضيوف" : "Guests"}: {order.guestCount ?? "—"}
            </span>
          </div>
          {!tableNumber ? (
            <span
              className="cashier-order-card__missing"
              data-testid={`open-order-card-missing-identity-${order.orderId}`}
            >
              {ar ? "افتح الطلب وحدد الطاولة" : "Open the order and assign a table"}
            </span>
          ) : null}
        </section>
      ) : (
        <section className="cashier-order-card__identity cashier-order-card__identity--customer">
          <span className="cashier-order-card__eyebrow">
            {order.serviceType === "Delivery"
              ? ar ? "عميل التوصيل" : "Delivery customer"
              : ar ? "اسم العميل" : "Customer name"}
          </span>
          <strong
            className="cashier-order-card__primary"
            data-testid={`open-order-card-customer-name-${order.orderId}`}
          >
            {customerName ?? (
              order.serviceType === "Delivery"
                ? ar ? "طلب توصيل" : "Delivery order"
                : ar ? "عميل اختياري" : "Optional customer"
            )}
          </strong>
          <div className="cashier-order-card__contact-grid">
            {customerPhone ? (
              <span
                className="cashier-order-card__contact"
                data-testid={`open-order-card-phone-${order.orderId}`}
              >
                <b>{ar ? "الهاتف" : "Phone"}</b>
                <span dir="ltr">{customerPhone}</span>
              </span>
            ) : null}
            {/* Legacy v9 source-contract compatibility: Car / السيارة. Visible terminology is Vehicle / المركبة. */}
            {vehicleNumber ? (
              <span
                className="cashier-order-card__contact cashier-order-card__contact--vehicle"
                data-testid={`open-order-card-vehicle-${order.orderId}`}
              >
                <b>{ar ? "المركبة" : "Vehicle"}</b>
                <span dir="ltr">{vehicleNumber}</span>
              </span>
            ) : null}
          </div>
          {identityMissing ? (
            <span
              className="cashier-order-card__missing"
              data-testid={`open-order-card-missing-identity-${order.orderId}`}
            >
              {order.serviceType === "Delivery"
                ? ar
                  ? "افتح الطلب وأكمل بيانات التوصيل"
                  : "Open the order and complete delivery details"
                : ar
                  ? "افتح الطلب وأكمل بيانات الاستلام"
                  : "Open the order and complete pickup details"}
            </span>
          ) : null}
        </section>
      )}

      <div className="cashier-order-card__items" aria-label={ar ? "ملخص الأصناف" : "Item summary"}>
        {previewLines.length ? (
          previewLines.map((line) => (
            <span key={line.orderLineId}>
              <strong>{ar ? line.productNameAr : line.productNameEn}</strong>
              <small>× {line.quantityMilli / 1000}</small>
            </span>
          ))
        ) : (
          <small>{ar ? "لم تُضف أصناف بعد" : "No items yet"}</small>
        )}
        {remainingItems > 0 ? (
          <small className="cashier-order-card__more">
            +{remainingItems} {ar ? "أصناف أخرى" : "more items"}
          </small>
        ) : null}
      </div>

        <div className="cashier-order-card__footer">
          <span>
            <small>{ar ? "الطلب" : "Order"}</small>
            <strong>{displayOrderNumber(order.orderNumber)}</strong>
          </span>
          <strong className="cashier-order-card__total">
            {formatMoney(order.totalMinor, order.currency)}
          </strong>
        </div>
      </button>
      <button
        type="button"
        className="cashier-order-card__prebill"
        data-testid={`print-prebill-${order.orderId}`}
        disabled={printingPreBill || order.itemCount === 0}
        onClick={onPrintPreBill}
      >
        <span aria-hidden="true">▣</span>
        {printingPreBill
          ? ar ? "جارٍ التجهيز…" : "Preparing…"
          : ar ? "طباعة الفاتورة" : "Print invoice"}
      </button>
    </article>
  );
}
